import { Injectable } from '@angular/core';
import { BehaviorSubject, interval, Observable, Subject, timer } from 'rxjs';
import { filter, scan, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { environment } from "src/environments/environment";

@Injectable()
export class InactivityTimerService {
  private readonly INACTIVITY_MS = environment.inactivityMs;
  private readonly COUNTDOWN_SECS = environment.countdownSecs;

  private readonly _showWarning$ = new BehaviorSubject<boolean>(false);
  private readonly _countdown$ = new BehaviorSubject<number>(this.COUNTDOWN_SECS);
  private readonly _timedOut$ = new Subject<void>();

  private readonly activity$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();

  readonly showWarning$: Observable<boolean> = this._showWarning$.asObservable();
  readonly countdown$: Observable<number> = this._countdown$.asObservable();
  readonly timedOut$: Observable<void> = this._timedOut$.asObservable();

  /**
   * Start the inactivity timer. Call once after the first message is sent.
   * Starts a timer based on the environment configuration; on expiry, shows a countdown warning.
   */
  start(): void {
    this.activity$.pipe(
      startWith(undefined as void),
      switchMap(() =>
        timer(this.INACTIVITY_MS).pipe(
          tap(() => {
            this._showWarning$.next(true);
            this._countdown$.next(this.COUNTDOWN_SECS);
          }),
          switchMap(() => {
            const countdownStartTime = Date.now();
            return interval(1000).pipe(
              tap(() => {
                const elapsedSecs = Math.floor((Date.now() - countdownStartTime) / 1000);
                const remaining = Math.max(0, this.COUNTDOWN_SECS - elapsedSecs);
                this._countdown$.next(remaining);
              }),
              filter(() => {
                const elapsedSecs = Math.floor((Date.now() - countdownStartTime) / 1000);
                return elapsedSecs >= this.COUNTDOWN_SECS;
              }),
              take(1)
            );
          })
        )
      ),
      tap(() => this._timedOut$.next()),
      takeUntil(this.destroy$)
    ).subscribe();
  }

  /**
   * Reset the inactivity timer. Call on each subsequent message sent.
   * If the warning is showing, it will be dismissed and the full timer restarts based on the environment configuration.
   */
  reset(): void {
    this._showWarning$.next(false);
    this.activity$.next();
  }

  /**
   * Stop all timers and clean up. Call on component destroy or manual close.
   */
  stop(): void {
    this.destroy$.next();
    this._showWarning$.next(false);
  }
}
